/* eslint-disable prefer-template */
/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState, useMemo } from "react";
import { Button, Input, message, Skeleton, Card, Select } from "antd";
import { SortAscendingOutlined } from "@ant-design/icons";
import { debounce } from "lodash";
import { toast } from "sonner";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Container, BodyContainer } from "../../styles/ContainerStyles";
import HeaderComponent from "../../components/Header/Header";
import { InformationContainer, InnerContainer } from "./styles";
import NewCompanyForm from "../../components/Forms/NewCompanyForm";
import {
  useAddCompany,
  useGetPaginatedCompanyCardsData,
} from "../../hooks/useCompanies";
import { CompanyCardV2 } from "../../components/Cards/CompanyCardv2/companyCardv2";

const Companies = () => {
  const { Search } = Input;
  const { Option } = Select;
  const addCompany = useAddCompany();
  const [nextPage, setNextPage] = useState<number>(1);
  const [searchCompany, setSearchCompany] = useState<string | null>("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const limit = 6;

  const [openNewCompanyDrawer, setOpenNewCompanyDrawer] = useState(false);

  // Fetch paginated companies from server
  const {
    data: companyData,
    isLoading,
    isFetching,
    refetch,
  } = useGetPaginatedCompanyCardsData(nextPage, limit, searchCompany);

  // Filter by status client-side if needed
  const filteredAndPaginatedData = useMemo(() => {
    if (!companyData) {
      return {
        data: [],
        totalCount: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
      };
    }

    // If no status filter, return the server data as-is
    if (statusFilter === "all") {
      return companyData;
    }

    // Filter by status client-side
    let filtered = [...companyData.data];

    if (statusFilter === "active") {
      filtered = filtered.filter((company: ICompanyCard) => {
        const status = (company as any).companyStatus;
        return status !== 0;
      });
    } else if (statusFilter === "inactive") {
      filtered = filtered.filter((company: ICompanyCard) => {
        const status = (company as any).companyStatus;
        return status === 0;
      });
    }

    // Recalculate pagination for filtered results
    const totalCount = filtered.length;
    const totalPages = Math.ceil(totalCount / limit) || 1;

    return {
      data: filtered,
      totalCount,
      totalPages,
      hasNextPage: companyData.hasNextPage,
      hasPreviousPage: companyData.hasPreviousPage,
    };
  }, [companyData, statusFilter, limit]);

  const handleCloseDrawer = async () => {
    setOpenNewCompanyDrawer(false);
    addCompany.reset();
  };

  const handleAddCompany = async (company: IAddCompany) => {
    if (company.companyImage && company.companyName && company.pricePerMonth) {
      try {
        await addCompany.mutateAsync(company);
        refetch();
        setOpenNewCompanyDrawer(false);
      } catch (error) {
        console.log(error);
      }
    } else {
      message.error("Please ensure all fields are filed correctly");
    }
  };

  const handleNextPage = () => {
    if (filteredAndPaginatedData.hasNextPage) {
      setNextPage(nextPage + 1);
    }
  };

  const handlePrevious = () => {
    if (filteredAndPaginatedData.hasPreviousPage) {
      setNextPage(nextPage - 1);
    }
  };

  const handleGoTo = debounce((pageNo: number) => {
    if (pageNo >= 1 && pageNo <= filteredAndPaginatedData.totalPages) {
      setNextPage(pageNo);
    } else {
      toast.error(
        "Please enter a valid page number within the range of 1 - " +
          filteredAndPaginatedData.totalPages
      );
    }
  }, 300);

  const handleRefresh = () => {
    setSearchCompany("");
    setNextPage(1);
    refetch();
  };

  const handleSearch = (value: string) => {
    if (value.length >= 3) {
      setSearchCompany(value);
      setNextPage(1);
    } else {
      toast.error("Please enter at least 3 characters to search");
    }
  };

  const handleCompanyUpdate = () => {
    refetch();
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setNextPage(1);
  };

  return (
    <div className="w-full h-full">
      <Container className="w-full h-full">
        <BodyContainer className="w-full h-full">
          <HeaderComponent title="Companies" />
          {isLoading || isFetching ? (
            <div style={{ width: "100%", height: "100%", flex: 1 }}>
              <InformationContainer>
                <div className="flex w-full justify-between pr-4 flex-row gap-1">
                  <div className="flex w-full justify-end pr-4 flex-row">
                    <Skeleton.Input active size="large" className="w-3" />
                  </div>
                  <Skeleton.Button active size="large" className="w-2" />
                </div>
                <InnerContainer>
                  <div className="card-container">
                    {Array.from({ length: 6 }).map((_, index) => (
                      <div
                        key={`skeleton-${index}`}
                        className="w-full max-w-2xl mx-auto"
                      >
                        <Card className="hover:shadow-lg transition-shadow duration-300 relative">
                          <div className="px-4 pt-4 pb-2">
                            <div className="flex items-center gap-4">
                              <Skeleton.Avatar
                                size={80}
                                shape="square"
                                active
                              />
                              <div className="flex-1 min-w-0">
                                <Skeleton
                                  active
                                  paragraph={false}
                                  title={{ width: "60%" }}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="px-4 pb-4 space-y-4">
                            <div className="flex justify-between gap-4">
                              <div className="flex-1 p-3 rounded-lg border">
                                <Skeleton
                                  active
                                  paragraph={false}
                                  title={{ width: "80%" }}
                                />
                                <div className="mt-2">
                                  <Skeleton
                                    active
                                    paragraph={false}
                                    title={{ width: "40%" }}
                                  />
                                </div>
                              </div>
                              <div className="flex-1 p-3 rounded-lg border">
                                <Skeleton
                                  active
                                  paragraph={false}
                                  title={{ width: "60%" }}
                                />
                                <div className="mt-2">
                                  <Skeleton
                                    active
                                    paragraph={false}
                                    title={{ width: "40%" }}
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="flex justify-between gap-4">
                              <div className="flex-1">
                                <Skeleton
                                  active
                                  paragraph={false}
                                  title={{ width: "50%" }}
                                />
                                <div className="mt-2 p-3 bg-gray-50 rounded-md">
                                  <Skeleton
                                    active
                                    paragraph={false}
                                    title={{ width: "70%" }}
                                  />
                                </div>
                              </div>
                              <div className="flex-1">
                                <Skeleton
                                  active
                                  paragraph={false}
                                  title={{ width: "50%" }}
                                />
                                <div className="mt-2 p-3 bg-gray-50 rounded-md">
                                  <Skeleton
                                    active
                                    paragraph={false}
                                    title={{ width: "70%" }}
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="flex justify-center">
                              <div className="text-center p-3">
                                <Skeleton
                                  active
                                  paragraph={false}
                                  title={{ width: "60px" }}
                                />
                                <div className="mt-2">
                                  <Skeleton
                                    active
                                    paragraph={false}
                                    title={{ width: "40px" }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card>
                      </div>
                    ))}
                  </div>
                </InnerContainer>
              </InformationContainer>
            </div>
          ) : (
            <div style={{ width: "100%", height: "100%", flex: 1 }}>
              <InformationContainer>
                <div className="flex w-full justify-between pr-4 flex-row gap-1">
                  <div className="flex w-full justify-end pr-4 flex-row gap-2 items-center">
                    {searchCompany && searchCompany?.length > 0 && (
                      <button
                        type="button"
                        style={{ marginRight: 2 }}
                        onClick={handleRefresh}
                        className="text-sm text-muted-foreground hover:text-foreground cursor-pointer mr-1"
                      >
                        Clear Search
                      </button>
                    )}

                    <Search
                      placeholder="Search for a Company"
                      allowClear
                      enterButton
                      size="large"
                      className="w-3"
                      onSearch={(value: string) => handleSearch(value)}
                    />

                    <Select
                      value={statusFilter}
                      onChange={handleStatusFilterChange}
                      suffixIcon={<SortAscendingOutlined />}
                      style={{ width: 120, borderRadius: "4px" }}
                    >
                      <Option value="all">No filter</Option>
                      <Option value="active">Active</Option>
                      <Option value="inactive">Inactive</Option>
                    </Select>
                  </div>

                  <Drawer
                    open={openNewCompanyDrawer}
                    onOpenChange={setOpenNewCompanyDrawer}
                  >
                    <DrawerTrigger asChild>
                      <Button type="primary" size="large" className="w-2">
                        Create New Company
                      </Button>
                    </DrawerTrigger>
                    <DrawerContent className="w-1/2 max-w-none">
                      <DrawerHeader>
                        <DrawerTitle className="text-2xl font-bold">
                          Create New Company
                        </DrawerTitle>
                      </DrawerHeader>

                      <div className="flex-1 overflow-y-auto px-4">
                        <NewCompanyForm
                          onFormFinish={handleAddCompany}
                          onModalClose={handleCloseDrawer}
                          isLoading={addCompany.isPending}
                          isSuccess={addCompany.isSuccess}
                        />
                      </div>
                    </DrawerContent>
                  </Drawer>
                </div>

                <div className="mt-4 flex justify-end pr-4">
                  <a
                    href="https://safercitystorage.blob.core.windows.net/safercityfiles/b6a84cd8-e71f-45b2-a9e7-4c010ca2cb50-Company_Upload_Template.xlsx"
                    download
                    className="inline-flex items-center text-sm font-medium text-gray-700 border-none hover:text-appMainColor whitespace-nowrap"
                  >
                    <svg
                      className="w-5 h-5 mr-1 group-hover:stroke-appMainColor"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                      />
                    </svg>
                    Download Company Member Upload Template
                  </a>
                </div>

                <InnerContainer>
                  <div className="card-container">
                    {Array.isArray(filteredAndPaginatedData.data) &&
                      filteredAndPaginatedData.data.map(
                        (card: ICompanyCard) => (
                          <CompanyCardV2
                            key={card.companyId}
                            companyData={{
                              id: card.companyId,
                              companyName: card.companyName,
                              imgSrc: card.companyImage,
                              companyLogo: card.companyLogo,
                              corporateAgreement: card.companyAgreement,
                              referralCode: card.referralCode || "N/A",
                              communityCode: card.communityCode || "N/A",
                              turnover: card.turnover,
                              pricePerMonth: `R${card.pricePerMonth}`,
                            }}
                          />
                        )
                      )}
                  </div>
                </InnerContainer>
              </InformationContainer>

              <div className="w-full flex flex-row pl-3 pr-3 justify-end items-center pb-2">
                <Button
                  type="primary"
                  ghost
                  size="large"
                  className="w-1"
                  disabled={!filteredAndPaginatedData.hasPreviousPage}
                  onClick={handlePrevious}
                >
                  Previous
                </Button>

                <div className="w-1/5 flex flex-row justify-content-center align-items-center gap-1">
                  <Input
                    size="middle"
                    style={{ width: 50, marginRight: 2, fontSize: "16px" }}
                    value={nextPage}
                    readOnly
                  />
                  <span style={{ fontSize: "17px" }}> Of </span>
                  <Input
                    size="middle"
                    style={{ width: 50, marginLeft: 2, fontSize: "16px" }}
                    value={
                      filteredAndPaginatedData.totalPages === 0
                        ? 1
                        : filteredAndPaginatedData.totalPages
                    }
                    readOnly
                  />
                  <span style={{ fontSize: "17px" }}> Go To </span>
                  <Input
                    size="middle"
                    style={{ width: 50, marginLeft: 2, fontSize: "16px" }}
                    onChange={(e) => handleGoTo(Number(e.target.value))}
                  />
                </div>

                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isLoading}
                  size="large"
                  className="w-1 bg-[#1757a2] shadow-none justify-center"
                  disabled={!filteredAndPaginatedData.hasNextPage}
                  onClick={handleNextPage}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </BodyContainer>
      </Container>
    </div>
  );
};

export default Companies;
